/**
 * Toast 服务
 * 提供全局的提示消息功能
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

// Toast 类型
export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading'

// Toast 位置类型
export type ToastPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center' | 'center'

// Toast 项接口
export interface ToastItem {
  id: string
  type: ToastType
  title?: string
  message: string
  duration?: number
  position?: ToastPosition
  stackable?: boolean
  timestamp: number
  isPersistent?: boolean
}

// Toast 状态接口
interface ToastState {
  toasts: ToastItem[]
  loadingId: string | null
  config: {
    position: ToastPosition
    stackable: boolean
  }
  addToast: (toast: Omit<ToastItem, 'id' | 'timestamp'>) => string
  updateToast: (id: string, updates: Partial<Omit<ToastItem, 'id' | 'timestamp'>>) => void
  removeToast: (id: string) => void
  clearToasts: () => void
  setConfig: (config: Partial<ToastState['config']>) => void
  setLoadingId: (id: string | null) => void
}

// 生成唯一 ID
const generateId = (): string => {
  return `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

// 创建 Toast 存储
export const useToastStore = create<ToastState>()(
  devtools(
    (set, get) => ({
      toasts: [],
      loadingId: null,
      config: {
        position: 'top-right',
        stackable: true
      },

      addToast: (toast) => {
        const state = get()
        const id = generateId()
        const newToast: ToastItem = {
          ...toast,
          id,
          timestamp: Date.now(),
          duration: toast.duration || 3000, // 默认 3 秒
          position: toast.position || state.config.position,
          stackable: toast.stackable !== undefined ? toast.stackable : state.config.stackable,
        }

        set((state) => ({
          toasts: [...state.toasts, newToast],
        }))

        // 如果不是持久性的，自动移除
        if (!newToast.isPersistent) {
          setTimeout(() => {
            get().removeToast(newToast.id)
          }, newToast.duration)
        }

        return id
      },

      updateToast: (id, updates) => {
        set((state) => ({
          toasts: state.toasts.map((t) => (t.id === id ? { ...t, ...updates } : t)),
        }))
      },

      removeToast: (id) => {
        set((state) => ({
          toasts: state.toasts.filter((toast) => toast.id !== id),
        }))
      },

      clearToasts: () => {
        set({ toasts: [] })
      },

      setConfig: (config) => {
        set((state) => ({
          config: { ...state.config, ...config }
        }))
      },

      setLoadingId: (id) => {
        set({ loadingId: id })
      }
    }),
    { name: 'ToastStore' }
  )
)

// Toast 工具函数
export const toast = {
  success: (message: string, options?: { title?: string, position?: ToastPosition, stackable?: boolean, duration?: number }) => {
    return useToastStore.getState().addToast({ type: 'success', message, ...options })
  },

  error: (message: string, options?: { title?: string, position?: ToastPosition, stackable?: boolean, duration?: number }) => {
    return useToastStore.getState().addToast({ type: 'error', message, duration: 5000, ...options })
  },

  warning: (message: string, options?: { title?: string, position?: ToastPosition, stackable?: boolean, duration?: number }) => {
    return useToastStore.getState().addToast({ type: 'warning', message, ...options })
  },

  info: (message: string, options?: { title?: string, position?: ToastPosition, stackable?: boolean, duration?: number }) => {
    return useToastStore.getState().addToast({ type: 'info', message, ...options })
  },

  loading: (message: string, options?: { title?: string, position?: ToastPosition, stackable?: boolean }) => {
    const store = useToastStore.getState()
    
    // 如果已有 loading，直接更新内容
    if (store.loadingId) {
      store.updateToast(store.loadingId, { message, ...options })
      return store.loadingId
    }

    const id = store.addToast({
      type: 'loading',
      message,
      isPersistent: true,
      duration: 0,
      position: 'top-center', // 默认居中显示
      ...options
    })
    
    store.setLoadingId(id)
    return id
  },

  hideLoading: () => {
    console.log('hideLoading >>>>> ')
    const store = useToastStore.getState()
    if (store.loadingId) {
      store.removeToast(store.loadingId)
      store.setLoadingId(null)
    }
  },

  // 带错误码的错误提示
  errorWithCode: (errorCode: number, errorInfo: string, options?: { position?: ToastPosition, stackable?: boolean }) => {
    const message = `[${errorCode}] ${errorInfo}`
    return useToastStore.getState().addToast({ type: 'error', message, duration: 5000, ...options })
  },

  // 清除所有提示
  clear: () => {
    useToastStore.getState().clearToasts()
  },

  // 设置全局配置
  setConfig: (config: { position?: ToastPosition, stackable?: boolean }) => {
    useToastStore.getState().setConfig(config)
  },
}

export default toast
